import { IconPlus, IconSearch, IconSelector, IconX } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type React from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverPopup, PopoverTrigger } from "@/components/ui/popover";
import { useShopScope } from "@/hooks/use-shop-scope";
import { fetchAmazonAccounts } from "@/lib/amazon-api";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import {
  buildScopeTree,
  checkStateOf,
  filterScopeTree,
  filterSwitcherConnections,
  initialExpandedAccounts,
  marketplaceCountryName,
  type ScopeShop,
  summarizeScope,
  toggleShops,
  unconnectedMarketplaceFor,
} from "./shop-scope/model";
import { ScopeSummaryContent, summaryText } from "./shop-scope/parts";
import { MultiScopeTree, type ScopeTreeLabels, SingleScopeList } from "./shop-scope/scope-tree";

export { filterSwitcherConnections, flagEmoji, marketplaceCode } from "./shop-scope/model";

/**
 * The global shop scope control (Paper "Global control · Shop scope").
 *
 * Multiple mode is a VIEW filter for aggregated reads and never touches the
 * operational `currentShopId`: the popover edits a draft over the account →
 * marketplace tree and only Apply writes the scope, so pages don't refetch on
 * every click. An empty selection means "all shops". Single mode reuses the
 * same tree and search for workflows that must target exactly one Shop.
 */

interface ShopScopeSelectProps {
  selectionMode?: "multiple" | "single";
  value?: string;
  onValueChange?: (shopId: string) => void;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
}

const TRIGGER_CLASS =
  "group/trigger inline-flex h-8 min-w-0 max-w-full shrink-0 cursor-pointer items-center gap-2 rounded-sm border border-control-border bg-control-default px-2 text-sm font-medium text-ink outline-none transition-[background-color,border-color,box-shadow] duration-[var(--duration-fast)] hover:border-control-border-hover hover:bg-control-hover aria-expanded:border-control-border-hover aria-expanded:bg-control-open focus-visible:border-focus-ring focus-visible:ring-2 focus-visible:ring-focus-halo disabled:cursor-not-allowed disabled:border-line disabled:bg-subtle disabled:opacity-60";

const NAV_SELECTOR = "[data-scope-nav]";

export function ShopScopeSelect({
  selectionMode = "multiple",
  value,
  onValueChange,
  disabled = false,
  ariaLabel,
  className,
}: ShopScopeSelectProps = {}) {
  const { t, i18n } = useTranslation("dashboard");
  const { scopeShopIds, workspaceShops, isAll } = useShopScope();
  const setSelectedShopIds = useAuthStore((s) => s.setSelectedShopIds);
  const activeWorkspaceId = useAuthStore((s) => s.user?.activeWorkspaceId ?? null);
  const accountsQuery = useQuery({
    queryKey: ["amazon", "accounts"],
    queryFn: fetchAmazonAccounts,
    staleTime: 60_000,
  });
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState<Set<string>>(() => new Set());
  // User expand/collapse choices; accounts without one follow the default, which
  // can only be computed once the accounts response has landed.
  const [expandOverrides, setExpandOverrides] = useState<ReadonlyMap<string, boolean>>(
    () => new Map(),
  );
  const searchRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const single = selectionMode === "single";
  const language = i18n.resolvedLanguage ?? i18n.language;

  const connections = accountsQuery.data?.connections;
  const tree = useMemo(
    () =>
      buildScopeTree(
        filterSwitcherConnections(connections ?? [], activeWorkspaceId),
        workspaceShops,
      ),
    [connections, activeWorkspaceId, workspaceShops],
  );
  const countryName = useCallback(
    (code: string) => marketplaceCountryName(code, language),
    [language],
  );
  const filtered = useMemo(
    () => filterScopeTree(tree, search, countryName),
    [tree, search, countryName],
  );

  if (workspaceShops.length === 0 || (!single && workspaceShops.length === 1)) return null;

  const appliedIds = single ? (value ? [value] : []) : scopeShopIds;
  const applied = new Set(appliedIds);
  const summary = summarizeScope(tree, appliedIds, !single && isAll);
  const attention = tree.shops.some((shop) => shop.needsReconnect && applied.has(shop.shopId));
  const label = summaryText(summary, t);
  const accessibleLabel = [
    `${ariaLabel ?? t("shopScope.title")}: ${label}`,
    attention ? t("shopScope.needsReconnect") : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const searching = search.trim().length > 0;
  const labels: ScopeTreeLabels = { t, countryName };
  const totalShops = tree.shops.length;
  const defaultExpanded = initialExpandedAccounts(tree, applied, !single && isAll);
  const expanded = new Set(
    tree.accounts
      .filter((account) => expandOverrides.get(account.id) ?? defaultExpanded.has(account.id))
      .map((account) => account.id),
  );

  function handleOpenChange(next: boolean) {
    if (next) {
      // Every open starts from the applied scope; closing without Apply discards edits.
      const selected = new Set(single ? (value ? [value] : []) : scopeShopIds);
      setDraft(selected);
      setSearch("");
      setExpandOverrides(new Map());
    }
    setOpen(next);
  }

  function toggleExpanded(accountId: string, force?: boolean) {
    setExpandOverrides((prev) => {
      const next = new Map(prev);
      next.set(accountId, force ?? !expanded.has(accountId));
      return next;
    });
  }

  function apply() {
    // Empty or every selectable Shop → "all" (the store drops the key so future
    // Shops join automatically); reconnect-blocked Shops can't hold "All" hostage.
    const coversAll = tree.shops.every((shop) => shop.needsReconnect || draft.has(shop.shopId));
    setSelectedShopIds(coversAll ? [] : [...draft]);
    setOpen(false);
  }

  function pick(shopId: string) {
    if (shopId !== value) onValueChange?.(shopId);
    setOpen(false);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (!single && event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      apply();
      return;
    }
    const panel = panelRef.current;
    const searchInput = searchRef.current;
    if (!panel) return;
    const active = document.activeElement as HTMLElement | null;
    const items = [...panel.querySelectorAll<HTMLElement>(NAV_SELECTOR)].filter(
      (el) => !el.hasAttribute("disabled") && el.getAttribute("aria-disabled") !== "true",
    );
    const index = active ? items.indexOf(active) : -1;
    const inSearch = active === searchInput;

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (index === -1 && !inSearch) return;
      event.preventDefault();
      const down = event.key === "ArrowDown";
      const nextIndex = down ? Math.min(index + 1, items.length - 1) : index - 1;
      if (nextIndex < 0) searchInput?.focus();
      else items[nextIndex]?.focus();
      return;
    }
    if ((event.key === "ArrowRight" || event.key === "ArrowLeft") && active) {
      const accountId = active.dataset.scopeAccount;
      if (!accountId || searching) return;
      event.preventDefault();
      toggleExpanded(accountId, event.key === "ArrowRight");
      return;
    }
    // Typing anywhere in the list goes to search.
    if (
      !inSearch &&
      event.key.length === 1 &&
      event.key !== " " &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey
    ) {
      searchInput?.focus();
    }
  }

  const accountsInDraft = tree.accounts.filter((account) =>
    account.shops.some((shop) => draft.has(shop.shopId)),
  ).length;
  const footerText = searching
    ? `${t("shopScope.matchCount", { count: filtered.matchCount })} · ${t("shopScope.selectedCount", { count: draft.size })}`
    : draft.size === 0
      ? t("shopScope.nothingSelected", { count: totalShops })
      : [
          t("shopScope.selectionOfTotal", { selected: draft.size, count: totalShops }),
          accountsInDraft > 0 ? t("shopScope.accountCount", { count: accountsInDraft }) : null,
        ]
          .filter(Boolean)
          .join(" · ");
  const universeText = [
    t("shopScope.shopCount", { count: totalShops }),
    tree.accounts.length > 0 ? t("shopScope.accountCount", { count: tree.accounts.length }) : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const missingMarketplace =
    filtered.matchCount === 0 ? unconnectedMarketplaceFor(tree, search, countryName) : null;
  const allState = checkStateOf(tree.shops, draft);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        disabled={disabled}
        aria-label={accessibleLabel}
        title={label}
        className={cn(TRIGGER_CLASS, className)}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          <ScopeSummaryContent summary={summary} t={t} />
        </span>
        {attention ? (
          <span className="size-1.5 shrink-0 rounded-full bg-warning" aria-hidden />
        ) : null}
        <IconSelector
          className="size-3.5 shrink-0 text-muted-foreground group-aria-expanded/trigger:text-ink"
          stroke={1.75}
          aria-hidden
        />
      </PopoverTrigger>
      <PopoverPopup
        align="start"
        sideOffset={4}
        initialFocus={searchRef}
        className="w-[400px] max-w-[calc(100vw-2rem)] overflow-hidden [&>[data-slot=popover-viewport]]:p-0"
      >
        {/* biome-ignore lint/a11y/noStaticElementInteractions: delegates arrow/typing/⌘↵ keys from the focusable rows and search inside it. */}
        <div ref={panelRef} onKeyDown={handleKeyDown} className="flex flex-col">
          <div className="flex h-10 shrink-0 items-center gap-2 border-b border-line px-3">
            <IconSearch
              className={cn(
                "size-[15px] shrink-0",
                searching ? "text-ink" : "text-muted-foreground",
              )}
              stroke={1.75}
              aria-hidden
            />
            <input
              ref={searchRef}
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("shopScope.searchPlaceholder")}
              aria-label={t("shopScope.searchPlaceholder")}
              autoComplete="off"
              className="h-full min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-placeholder [&::-webkit-search-cancel-button]:appearance-none"
            />
            {search ? (
              <button
                type="button"
                aria-label={t("shopScope.clearSearch")}
                onClick={() => {
                  setSearch("");
                  searchRef.current?.focus();
                }}
                className="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-xs text-muted-foreground outline-none hover:bg-state-hover hover:text-ink focus-visible:ring-2 focus-visible:ring-focus-ring"
              >
                <IconX className="size-3.5" stroke={2} aria-hidden />
              </button>
            ) : null}
          </div>

          {!single && !searching ? (
            <>
              {/* Full-bleed row: it spans the popover edge to edge like the search bar above. */}
              <div>
                {/* biome-ignore lint/a11y/noStaticElementInteractions: the checkbox is the accessible control; the row is a wider hit area. */}
                {/* biome-ignore lint/a11y/useKeyWithClickEvents: keyboard users operate the focusable checkbox itself. */}
                <div
                  onClick={() => setDraft(toggleShops(tree.shops, draft))}
                  className="group/row flex h-10 w-full cursor-pointer items-center gap-2 pr-3 pl-[34px] hover:bg-control-highlight has-[:focus-visible]:bg-control-highlight"
                >
                  <Checkbox
                    checked={allState === "checked"}
                    indeterminate={allState === "indeterminate"}
                    onCheckedChange={() => setDraft(toggleShops(tree.shops, draft))}
                    onClick={(event) => event.stopPropagation()}
                    aria-label={t("shopScope.allShops")}
                    data-scope-nav=""
                  />
                  <span className="min-w-0 flex-1 truncate text-xs font-semibold text-ink">
                    {t("shopScope.allShops")}
                  </span>
                  <span className="shrink-0 text-badge text-muted-foreground tabular-nums">
                    {universeText}
                  </span>
                </div>
              </div>
              <div className="h-px shrink-0 bg-line" />
            </>
          ) : null}

          <div
            data-testid="shop-scope-rows"
            {...(single
              ? { role: "radiogroup", "aria-label": ariaLabel ?? t("shopScope.title") }
              : {})}
            className="paper-scrollbar flex max-h-[360px] flex-col overflow-y-auto p-1"
          >
            {filtered.matchCount === 0 ? (
              <div className="flex flex-col items-center gap-1.5 px-6 py-7 text-center">
                <p className="text-sm font-medium text-ink">
                  {searching
                    ? t("shopScope.noMatchQuery", { query: search.trim() })
                    : t("shopScope.noMatch")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {missingMarketplace
                    ? t("shopScope.notConnectedMarketplace", {
                        country: countryName(missingMarketplace),
                      })
                    : t("shopScope.noMatchHint")}
                </p>
                <div className="flex pt-1.5">
                  <Button
                    variant="outline"
                    size="xs"
                    className="rounded-sm px-2.5"
                    render={<Link to="/settings" onClick={() => setOpen(false)} />}
                  >
                    <IconPlus className="size-[13px]" stroke={2} aria-hidden />
                    {t("shopScope.connectMarketplace")}
                  </Button>
                </div>
              </div>
            ) : single ? (
              <SingleScopeList
                filtered={filtered}
                searching={searching}
                value={value}
                expanded={expanded}
                disabled={disabled}
                labels={labels}
                onToggleExpanded={(id) => toggleExpanded(id)}
                onPick={pick}
              />
            ) : (
              <MultiScopeTree
                filtered={filtered}
                searching={searching}
                draft={draft}
                expanded={expanded}
                showOnly={{ accounts: tree.accounts.length > 1, shops: totalShops > 1 }}
                disabled={disabled}
                labels={labels}
                onToggleExpanded={(id) => toggleExpanded(id)}
                onToggleShops={(shops: readonly ScopeShop[]) => setDraft(toggleShops(shops, draft))}
                onOnly={(shops: readonly ScopeShop[]) =>
                  setDraft(
                    new Set(
                      shops.filter((shop) => !shop.needsReconnect).map((shop) => shop.shopId),
                    ),
                  )
                }
                onNavigate={() => setOpen(false)}
              />
            )}
          </div>

          {!single ? (
            <div className="flex shrink-0 items-center gap-2 border-t border-line px-3 py-2.5">
              <span
                className="min-w-0 flex-1 truncate text-xs text-text-secondary tabular-nums"
                aria-live="polite"
              >
                {footerText}
              </span>
              <Button
                variant="ghost"
                size="xs"
                className="rounded-sm px-2.5 text-text-secondary"
                onClick={() => setDraft(new Set())}
              >
                {t("shopScope.clear")}
              </Button>
              <Button size="xs" className="rounded-sm px-3" onClick={apply}>
                {t("shopScope.apply")}
              </Button>
            </div>
          ) : null}
        </div>
      </PopoverPopup>
    </Popover>
  );
}

/**
 * Single-Shop picker bound to the operational `currentShopId`, for pages that read and write
 * one ambient Shop and have no resource of their own to follow.
 */
export function CurrentShopSelect({
  disabled = false,
  disabledReason,
}: {
  disabled?: boolean;
  disabledReason?: string;
}) {
  const currentShopId = useAuthStore((s) => s.currentShopId);
  const setCurrentShop = useAuthStore((s) => s.setCurrentShop);
  const queryClient = useQueryClient();
  return (
    <span title={disabled ? disabledReason : undefined}>
      <ShopScopeSelect
        selectionMode="single"
        value={currentShopId ?? undefined}
        disabled={disabled}
        onValueChange={(shopId) => {
          setCurrentShop(shopId);
          void queryClient.invalidateQueries();
        }}
      />
    </span>
  );
}
